import { DateWeek } from "../../types/Event";
import { Roundtable, RoundtableRecurrence } from "../../types/Roundtable";

// The form reuses the Events building blocks, so dates arrive as Date objects
// (or ISO strings on edit). Normalize to ISO, the shape the app reads.
export const coerceIsoDate = (value: any): string | null => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }
  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  }
  if (typeof value === "object" && typeof value.toDate === "function") {
    const parsed = value.toDate();
    return parsed instanceof Date && !Number.isNaN(parsed.getTime())
      ? parsed.toISOString()
      : null;
  }
  return null;
};

// The Events weekday picker stores DateWeek objects with a per-day time. The
// roundtable model only needs the active weekday codes; the meeting time comes
// from startTime's time-of-day (same engine the app uses for occurrences).
export const mapRecurrence = (recurringDate: {
  isActive?: boolean;
  daysOfWeek?: DateWeek[];
}): RoundtableRecurrence | undefined => {
  if (!recurringDate?.isActive) return undefined;

  const daysOfWeek = (recurringDate.daysOfWeek || [])
    .filter((day) => day?.isActive)
    .map((day) => day.dayOfWeek);

  return { isActive: true, daysOfWeek };
};

export type RoundtableFormValues = {
  id?: string;
  title: string;
  goal: string;
  startTime: Date | string | null;
  endTime: Date | string | null;
  maxParticipants?: number | string | null;
  recurringDate?: { isActive?: boolean; daysOfWeek?: DateWeek[] };
};

// Shared field mapping for create and edit. Returns only the fields the form
// owns; create/edit layer the rest (id, createdBy, agenda, ...).
export const mapRoundtableFormValues = (
  values: RoundtableFormValues
): Pick<
  Roundtable,
  "title" | "goal" | "startTime" | "endTime" | "recurrence" | "maxParticipants"
> => {
  const startTime = coerceIsoDate(values.startTime);
  const endTime = coerceIsoDate(values.endTime);
  const capacity = Number(values.maxParticipants);

  return {
    title: (values.title || "").trim(),
    goal: (values.goal || "").trim(),
    startTime: startTime || new Date().toISOString(),
    endTime: endTime || new Date().toISOString(),
    recurrence: mapRecurrence(values.recurringDate || {}),
    maxParticipants:
      Number.isFinite(capacity) && capacity > 0 ? capacity : undefined,
  };
};
